/**
 * SortableColumn - wrapper to make KanbanColumn draggable for reordering
 */

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { clsx } from 'clsx';
import { cloneElement, isValidElement, type ReactElement } from 'react';

interface SortableColumnProps {
  id: string;
  children: ReactElement;
}

export default function SortableColumn({ id, children }: SortableColumnProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `sortable-column-${id}`,
    data: { type: 'sortable-column', columnId: id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Pass drag handle props to the child KanbanColumn
  const childWithDragHandle = isValidElement(children)
    ? cloneElement(children, {
        dragHandleProps: { ...attributes, ...listeners },
        isDragging,
      } as Record<string, unknown>)
    : children;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'flex-shrink-0',
        isDragging && 'z-50 opacity-50'
      )}
    >
      {childWithDragHandle}
    </div>
  );
}
